import React, { useState, useEffect, useCallback } from "react";
import {
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  Box,
  Grid,
  Collapse,
  Link,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useStyles } from "./styles";
import { AddressAutocomplete, type ResolvedAddress } from "../AddressAutocomplete/AddressAutocomplete";

/**
 * CustomAddressInput — saisie d'adresse du back-office.
 *
 * CE QUI A CHANGE, ET POURQUOI C'ETAIT URGENT
 * -------------------------------------------
 * Ce composant demandait a l'operateur de TAPER la latitude et la longitude.
 * C'est le champ qui alimente ModalAddSign, donc la table `sign` — celle que
 * toute la plateforme interroge ensuite : l'application mobile, l'application
 * flotte, le ciblage publicitaire. Une coordonnee fausse saisie ici se propage
 * partout et rien en aval ne peut la rattraper.
 *
 * Le detail qui transformait une saisie penible en saisie DANGEREUSE :
 *
 *     parseFloat(event.target.value) || 0
 *     latitude: lat || 0
 *
 * Un champ laisse vide ne valait pas NULL, il valait 0. Or (0, 0) est un point
 * REEL, au large du golfe de Guinee. Une enseigne enregistree ainsi n'etait pas
 * \u00ab sans position \u00bb — elle etait positionnee dans l'Atlantique : absente de la
 * carte, hors de portee de toute recherche par proximite, et impossible a
 * distinguer d'une saisie volontaire. `|| 0` avalait aussi toute latitude
 * valant exactement 0.
 *
 * Desormais : recherche d'adresse en premier (repertoire interne, puis
 * fournisseur externe cote serveur — voir api/place.ts), coordonnees remplies
 * automatiquement, et saisie manuelle reléguée derriere un depliant pour les
 * cas ou l'operateur a une position exacte a imposer.
 */

type Props = {
  label: string;
  placeholder?: string;
  /** Libelle de l'adresse. */
  value: string;
  lat: number;
  lng: number;
  /**
   * `lat` / `lng` valent 0 quand aucune position n'est connue.
   *
   * Le contrat n'a PAS ete change en `number | null` : ModalAddSign et l'API
   * derriere lui attendent des nombres, et elargir le type aurait demande une
   * migration coordonnee des deux cotes. Le composant garantit en revanche que
   * 0 n'est plus produit par accident — il ne sort que d'une saisie manuelle
   * explicite — et signale visuellement l'absence de position.
   */
  onChange: (addressData: { raw: string; lat: number; lng: number }) => void;
  onGooglePlaceIdChange?: (placeId: string) => void;
  error?: string;
  marginBottom?: string;
  disabled?: boolean;
  required?: boolean;
};

export const CustomAddressInput: React.FC<Props> = (props) => {
  const {
    label,
    placeholder,
    value,
    lat,
    lng,
    onChange,
    error,
    marginBottom,
    disabled = false,
    required = false,
  } = props;

  const { t } = useTranslation();
  const classes = useStyles(props);

  const [street, setStreet] = useState<string>(value || "");
  const [latitude, setLatitude] = useState<number>(lat || 0);
  const [longitude, setLongitude] = useState<number>(lng || 0);
  const [showManual, setShowManual] = useState(false);

  useEffect(() => {
    setStreet(value || "");
    setLatitude(lat || 0);
    setLongitude(lng || 0);
  }, [value, lat, lng]);

  /** Une position est \u00ab connue \u00bb des lors qu'elle n'est pas le point nul. */
  const hasPosition = !(latitude === 0 && longitude === 0);

  const emit = useCallback(
    (raw: string, nextLat: number, nextLng: number) => {
      setStreet(raw);
      setLatitude(nextLat);
      setLongitude(nextLng);
      onChange({ raw, lat: nextLat, lng: nextLng });
    },
    [onChange]
  );

  /** Un lieu a ete choisi : les coordonnees sont garanties par le composant. */
  const handleSelect = useCallback(
    (address: ResolvedAddress | null) => {
      if (!address) {
        emit(street, 0, 0);
        return;
      }
      emit(address.raw, address.lat, address.lng);
    },
    [emit, street]
  );

  /**
   * Texte libre : le libelle est conserve, la position REMISE A ZERO.
   *
   * Garder les coordonnees du lieu precedemment choisi pendant que l'operateur
   * reecrit l'adresse produirait le pire resultat possible : un libelle et un
   * point qui ne se correspondent plus, sans que rien ne le signale.
   */
  const handleFreeText = useCallback(
    (text: string) => {
      if (text === street) return;
      emit(text, 0, 0);
    },
    [emit, street]
  );

  const handleManualChange = (field: "lat" | "lng") => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const raw = event.target.value;

    /*
     * `parseFloat` seul, PAS `|| 0`.
     *
     * `|| 0` transformait aussi bien un champ vide qu'une latitude valant
     * exactement 0 en la meme valeur. Ici un champ vide (NaN) retombe sur 0
     * parce que le contrat du parent l'exige, mais un 0 SAISI est conserve tel
     * quel et le bandeau ci-dessous avertit qu'il n'y a pas de position.
     */
    const parsed = parseFloat(raw);
    const next = Number.isFinite(parsed) ? parsed : 0;

    if (field === "lat") emit(street, next, longitude);
    else emit(street, latitude, next);
  };

  return (
    <FormControl
      className={classes.formControl}
      error={!!error}
      disabled={disabled}
      style={{ marginBottom: marginBottom || "0px" }}
    >
      <InputLabel shrink>{label}</InputLabel>
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <AddressAutocomplete
              label={t("address.street", "Rue / Adresse")}
              value={street}
              onSelect={handleSelect}
              onFreeText={handleFreeText}
              placeholder={placeholder || "123 Rue de la Paix"}
              required={required}
              disabled={disabled}
              error={error}
              helperText={t(
                "address.searchHint",
                "Cherchez le lieu : les coordonnées sont renseignées automatiquement."
              )}
            />
          </Grid>

          <Grid item xs={12}>
            {hasPosition ? (
              <Alert severity="success" sx={{ py: 0.25 }}>
                {t("address.positionSet", {
                  lat: latitude.toFixed(6),
                  lng: longitude.toFixed(6),
                  defaultValue: `Position enregistrée : ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
                })}
              </Alert>
            ) : (
              /*
               * Ce bandeau est la contrepartie du contrat `number` : puisque
               * l'absence de position s'ecrit 0 en base, elle doit au moins
               * etre VISIBLE au moment de la saisie. Sans lui, l'operateur
               * enregistre une enseigne dans l'Atlantique en croyant
               * simplement ne pas avoir renseigne de coordonnees.
               */
              <Alert severity="warning" sx={{ py: 0.25 }}>
                {t(
                  "address.noPosition",
                  "Aucune position : cette adresse n'apparaîtra pas sur la carte et ne sortira dans aucune recherche par proximité."
                )}
              </Alert>
            )}
          </Grid>

          <Grid item xs={12}>
            <Link
              component="button"
              type="button"
              underline="hover"
              disabled={disabled}
              onClick={() => setShowManual((v) => !v)}
              sx={{ fontSize: 12 }}
            >
              {showManual
                ? t("address.hideManual", "Masquer les coordonnées")
                : t("address.showManual", "Saisir les coordonnées manuellement")}
            </Link>
          </Grid>

          <Grid item xs={12}>
            <Collapse in={showManual}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t("address.latitude", "Latitude")}
                    value={latitude}
                    onChange={handleManualChange("lat")}
                    variant="outlined"
                    fullWidth
                    size="small"
                    type="number"
                    disabled={disabled}
                    placeholder="48.8566"
                    inputProps={{ step: "any", min: -90, max: 90 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t("address.longitude", "Longitude")}
                    value={longitude}
                    onChange={handleManualChange("lng")}
                    variant="outlined"
                    fullWidth
                    size="small"
                    type="number"
                    disabled={disabled}
                    placeholder="2.3522"
                    inputProps={{ step: "any", min: -180, max: 180 }}
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Grid>
        </Grid>
      </Box>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};
